/**
 * Date and Timezone Utilities
 *
 * ShelfCycle Standard: Org timezone should ALWAYS drive display and date calculations,
 * NEVER the local browser timezone nor raw UTC.
 *
 * Key utilities:
 * - CalendarDay: Type-safe representation of a calendar day (no time/timezone ambiguity)
 * - pickerDateTo*: Convert DatePicker values to UTC instants
 * - calendarDay*: Convert explicit calendar days to UTC instants
 * - useOrgTimezone(): Hook to get org timezone (in ~/contexts/orgProvider)
 *
 * See docs/TIMEZONE_STANDARDS.md for comprehensive documentation and examples.
 *
 * @module datetime
 */
import { DateTime } from "luxon";

import { Nullable } from "~/shared/types/Nullable";

const dateFormat = "M/d/yyyy";

type Timezone = string | null | undefined;

const parseTimezone = (timezone: Timezone): string | undefined => timezone || undefined;

const convertDateTimeToDate = (input: DateTime): Date => new Date(input.toString());

export const convertDateToDateTime = (input: Date, timezone?: Timezone): DateTime =>
  DateTime.fromJSDate(input, { zone: parseTimezone(timezone) });

/**
 * Convert a JS `Date` object to a given string format
 * @see [Formatting Tokens](https://github.com/moment/luxon/blob/master/docs/formatting.md#table-of-tokens)
 */
export const convertDateToString = (input: Date, timezone?: Timezone, format: string = dateFormat): string =>
  convertDateToDateTime(input, timezone).toFormat(format);

/**
 * Update a JS `Date` object with a new one that properly adjusts the Timezone offset
 */
export const updateDateWithTimezone = (input: Date, timezone?: Timezone): Date => {
  const inputDateTime = convertDateToDateTime(input, timezone);
  return convertDateTimeToDate(inputDateTime);
};

export const getBeginningOfYearFromDate = (input: Date, timezone?: Timezone): Date => {
  const inputDateTime = convertDateToDateTime(input, timezone).startOf("year");
  return convertDateTimeToDate(inputDateTime);
};

/**
 * Accounting Date Filtering Standard:
 * All accounting reports must normalize dates using org timezone and start/end-of-day semantics.
 * - Period reports: normalize to start-of-day (inclusive) and end-of-day (inclusive when used with `<=` bounds) in org timezone.
 * - As-of reports: normalize "as of" date to end-of-day in org timezone.
 * See daily-income-statement.ts for the canonical implementation pattern.
 */
export const getBeginningOfDayFromDate = (input: Date, timezone?: Timezone): Date =>
  convertDateTimeToDate(convertDateToDateTime(input, timezone).startOf("day"));

export const getEndOfDayFromDate = (input: Date, timezone?: Timezone): Date =>
  convertDateTimeToDate(convertDateToDateTime(input, timezone).endOf("day"));

/**
 * Interpret a DateTimePicker value in org timezone.
 *
 * A browser DateTimePicker returns a JS Date whose wall-clock components
 * (year/month/day/hour/minute) reflect the browser's local timezone. For
 * accounting entries we want those same wall-clock components re-interpreted
 * as if the user is looking at org-timezone time.
 *
 * Example: browser = UTC-8, org = America/New_York (UTC-5)
 *   User picks  "2026-01-15 14:00"
 *   Picker emits 2026-01-15T22:00:00Z  (14:00 interpreted as UTC-8)
 *   This fn returns 2026-01-15T19:00:00Z (same 14:00 wall-clock in UTC-5)
 *
 * Apply to every custom-date DateTimePicker onChange before storing state.
 */
export const interpretPickerDateInOrgTimezone = (date: Date, timezone: string): Date =>
  convertDateTimeToDate(DateTime.fromJSDate(date).setZone(timezone, { keepLocalTime: true }));

/**
 * Map a stored accounting instant to a DateTimePicker value whose wall-clock matches org timezone.
 * Inverse of {@link interpretPickerDateInOrgTimezone} — seed picker state from DB timestamps.
 *
 * @see docs/TIMEZONE_STANDARDS.md — DateTimePicker (CostEditPostedAtSection / BillPostConfirmationModal)
 */
export const orgInstantToDateTimePickerValue = (instant: Date, timezone: string): Date => {
  const dt = DateTime.fromJSDate(instant, { zone: timezone });
  return new Date(dt.year, dt.month - 1, dt.day, dt.hour, dt.minute, dt.second, dt.millisecond);
};

/**
 * Normalizes a two-element nullable date range to org calendar-day instants for inclusive comparisons.
 * Saved filters often pass URL noon-UTC instants per picked calendar day (`useSavedFilter` urlInputDate); this expands
 * the start to org start-of-day and the end to org end-of-day so intraday DB timestamps stay in range (TIMEZONE_STANDARDS).
 *
 * @returns `undefined` when the range is missing, not length-2, or both bounds are null.
 */
export function normalizeNullableDateRangeToOrgDayBoundTimes(
  range: ReadonlyArray<Date | null> | null | undefined,
  timezone?: Timezone,
): [Date | null, Date | null] | undefined {
  if (!range || range.length !== 2) {
    return undefined;
  }
  const [start, end] = range;
  if (!start && !end) {
    return undefined;
  }
  return [start ? getBeginningOfDayFromDate(start, timezone) : null, end ? getEndOfDayFromDate(end, timezone) : null];
}

/** Prisma `createdAt` bounds for AP Costs "Created Date Range" (same normalization as lifecycle filters). */
export function normalizeApCostsCreatedDateRangeBounds(
  dateRange: ReadonlyArray<Date | null> | undefined | null,
  timezone?: Timezone,
): { dateStart: Date | null; dateEnd: Date | null } {
  const pair = normalizeNullableDateRangeToOrgDayBoundTimes(dateRange, timezone);
  if (!pair) {
    return { dateStart: null, dateEnd: null };
  }
  const [dateStart, dateEnd] = pair;
  return { dateStart, dateEnd };
}

export const getBeginningOfDayFromString = (input: string, timezone?: Timezone): Date => {
  const { year, month, day } = DateTime.fromISO(input, { zone: "UTC" });
  const luxonDate = DateTime.fromObject({ year, month, day }, { zone: parseTimezone(timezone) }).startOf("day");
  return convertDateTimeToDate(luxonDate);
};

export const parseIfDateString = (value: string): Nullable<Date> => {
  const dt = DateTime.fromJSDate(new Date(value));
  return dt.isValid ? dt.toJSDate() : null;
};

/**
 * Gets the timezone abbreviation (e.g., "CT", "ET", "PT") from an IANA timezone string.
 * Uses the provided date to determine DST-aware abbreviation.
 *
 * @param timezone - IANA timezone string (e.g., "America/Chicago")
 * @param date - Date to use for DST calculation (defaults to now)
 * @returns Timezone abbreviation (e.g., "CST", "CDT", "EST"), or empty string if unavailable
 */
export const getTimezoneAbbreviation = (timezone: string, date: Date = new Date()): string => {
  const dt = DateTime.fromJSDate(date, { zone: timezone });
  return dt.offsetNameShort ?? ""; // Return empty string if abbreviation unavailable to avoid rendering full IANA name
};

// ============================================================================
// Calendar Day Type - Semantic Clarity for Date-Only Values
// ============================================================================
//
// JS Date conflates two concepts: "calendar day" and "instant in time".
// This causes subtle bugs when timezone conversions are involved.
//
// Example bug: User picks "Jan 15" in a UTC browser. DatePicker returns midnight
// Jan 15 UTC. When interpreted in US/Central, that instant is Jan 14 at 6pm!
// Calling endOf("day") gives end of Jan 14 - wrong day.
//
// CalendarDay explicitly represents "a date on the calendar" with no time/timezone
// ambiguity. Functions accepting CalendarDay cannot be confused about semantics.

/**
 * Represents a calendar day without time or timezone ambiguity.
 * Use this instead of Date when you mean "a specific day" not "a specific instant".
 *
 * @example
 * // User selects January 15th - we only care about the day, not the time
 * const day: CalendarDay = { year: 2026, month: 1, day: 15 };
 */
export interface CalendarDay {
  year: number;
  /** Month 1-12 (January = 1, unlike JS Date where January = 0) */
  month: number;
  /** Day of month 1-31 */
  day: number;
}

/**
 * Extract the calendar day (year, month, day) from a Date, interpreting it in the given timezone.
 *
 * IMPORTANT: The timezone parameter determines which calendar day the Date falls on.
 * The same UTC instant can be different calendar days in different timezones!
 *
 * Use this when receiving a Date from a DatePicker and you need to know what calendar
 * day the user intended (in the org's timezone, not the browser's).
 *
 * @param date - A JS Date (typically from a DatePicker)
 * @param timezone - The timezone to interpret the date in (should be org timezone)
 * @returns The calendar day in the specified timezone
 *
 * @example
 * // User in UTC browser picks "Jan 15", DatePicker returns midnight Jan 15 UTC
 * // But org is in US/Central, and midnight UTC = Jan 14 6pm Central
 * const day = extractCalendarDay(pickerDate, "US/Central");
 * // day = { year: 2026, month: 1, day: 14 } - the ACTUAL day in org timezone!
 *
 * // To get the day the user SAW in the picker (browser timezone):
 * const browserDay = extractCalendarDay(pickerDate, "UTC");
 * // browserDay = { year: 2026, month: 1, day: 15 }
 */
export const extractCalendarDay = (date: Date, timezone: string): CalendarDay => {
  const dt = DateTime.fromJSDate(date, { zone: timezone });
  return { year: dt.year, month: dt.month, day: dt.day };
};

/**
 * Extract the calendar day from a Date using the browser's local timezone.
 *
 * Use this when you need to know what day the user SAW in a DatePicker.
 * DatePickers typically show dates in browser local time.
 *
 * @param date - A JS Date from a DatePicker
 * @returns The calendar day as displayed in the browser's timezone
 */
export const extractCalendarDayLocal = (date: Date): CalendarDay => {
  const dt = DateTime.fromJSDate(date); // Local timezone by default
  return { year: dt.year, month: dt.month, day: dt.day };
};

/**
 * Build a JS `Date` at **browser-local midnight** with the given Y-M-D, for Mantine `DateInput`
 * (`value`, `minDate`, `maxDate`).
 *
 * Do **not** pass raw org-boundary UTC instants (DB `confirmedAt`, `calendarDayStartOfDayUTC` output)
 * as `DateInput` props — browsers render instants in local TZ, causing off-by-one vs the org calendar.
 * Prefer {@link orgCalendarSemanticsToPickerDate} for persisted org fields.
 *
 * Submission still flows through {@link pickerDateToStartOfDayUTC}: local-visible day → org start-of-day UTC.
 *
 * @see docs/TIMEZONE_STANDARDS.md — DatePicker Pattern 1
 */
export const calendarDayToLocalPickerDate = (day: CalendarDay): Date => new Date(day.year, day.month - 1, day.day);

/**
 * Map a stored UTC `Date` to the same **org-calendar** `{ year, month, day }`, then encode it as local-midnight
 * for DateInput (labels match org day no matter where the user is browsing from).
 */
export const orgCalendarSemanticsToPickerDate = (instant: Date, timezone: string): Date =>
  calendarDayToLocalPickerDate(extractCalendarDay(instant, timezone));

/**
 * Returns true if two dates fall on different calendar days.
 * Uses the given timezone when provided, otherwise browser local timezone.
 *
 * Use for comparing scheduled vs actual dates at day granularity (e.g., ship-by tooltip).
 *
 * This function is defensive: if the timezone is invalid or Luxon rejects it,
 * it falls back to local timezone extraction to ensure UI helpers never crash.
 */
export const calendarDaysDiffer = (dateA: Date, dateB: Date, timezone?: Nullable<string>): boolean => {
  let dayA: CalendarDay;
  let dayB: CalendarDay;

  // Defensive: try using the provided timezone, fall back to local if invalid
  if (timezone) {
    try {
      dayA = extractCalendarDay(dateA, timezone);
      dayB = extractCalendarDay(dateB, timezone);
    } catch {
      // Invalid timezone - fall back to local extraction
      dayA = extractCalendarDayLocal(dateA);
      dayB = extractCalendarDayLocal(dateB);
    }
  } else {
    dayA = extractCalendarDayLocal(dateA);
    dayB = extractCalendarDayLocal(dateB);
  }

  return dayA.year !== dayB.year || dayA.month !== dayB.month || dayA.day !== dayB.day;
};

/**
 * Create a CalendarDay from an ISO date string (YYYY-MM-DD).
 *
 * @param isoDate - Date string in ISO format (e.g., "2026-01-15")
 * @returns CalendarDay representing that date
 * @throws {Error} If the date string is invalid
 */
export const calendarDayFromISO = (isoDate: string): CalendarDay => {
  const dt = DateTime.fromISO(isoDate);
  if (!dt.isValid) {
    throw new Error(`Invalid ISO date string: ${isoDate}`);
  }
  return { year: dt.year, month: dt.month, day: dt.day };
};

/**
 * Safely parse an ISO date string (YYYY-MM-DD) to a CalendarDay.
 * Returns null instead of throwing on invalid input.
 *
 * Use this when parsing user input, API responses, or other untrusted data.
 *
 * @param isoDate - Date string in ISO format (e.g., "2026-01-15")
 * @returns CalendarDay if valid, null otherwise
 */
export const tryCalendarDayFromISO = (isoDate: string): CalendarDay | null => {
  const dt = DateTime.fromISO(isoDate);
  if (!dt.isValid) {
    return null;
  }
  return { year: dt.year, month: dt.month, day: dt.day };
};

/**
 * Format a CalendarDay as an ISO date string (YYYY-MM-DD).
 */
export const calendarDayToISO = (day: CalendarDay): string =>
  `${day.year}-${String(day.month).padStart(2, "0")}-${String(day.day).padStart(2, "0")}`;

/**
 * Parse a timestamp string from Postgres `::text` output or ISO serialization.
 * Values are interpreted as UTC instants before org-calendar extraction.
 */
export const parseDbTimestampText = (value: string): Date => {
  const iso = DateTime.fromISO(value, { zone: "utc" });
  if (iso.isValid) {
    return iso.toJSDate();
  }
  const sql = DateTime.fromSQL(value, { zone: "utc" });
  if (sql.isValid) {
    return sql.toJSDate();
  }
  return new Date(value);
};

/** Map a DB/ISO timestamp string to the org-calendar day (YYYY-MM-DD). @see docs/TIMEZONE_STANDARDS.md */
export const timestampToOrgCalendarDayString = (value: string, timezone: string): string =>
  calendarDayToISO(extractCalendarDay(parseDbTimestampText(value), timezone));

/** Map a stored UTC instant to the org-calendar day (YYYY-MM-DD). @see docs/TIMEZONE_STANDARDS.md */
export const instantToOrgCalendarDayString = (instant: Date, timezone: string): string =>
  calendarDayToISO(extractCalendarDay(instant, timezone));

/** Sort comparator for two UTC instants by org calendar day (not by clock time). @see docs/TIMEZONE_STANDARDS.md */
export const compareOrgCalendarDayInstants = (left: Date, right: Date, timezone: string): number =>
  compareCalendarDays(extractCalendarDay(left, timezone), extractCalendarDay(right, timezone));

/** Prior org-calendar day for a YYYY-MM-DD string (calendar arithmetic, not instant-based). */
export const priorCalendarDayIso = (dateStr: string): string =>
  DateTime.fromISO(dateStr, { zone: "utc" }).minus({ days: 1 }).toFormat("yyyy-MM-dd");

/**
 * Compare two CalendarDay values (same contract as Array.prototype.sort comparator).
 * Returns negative if a is before b, 0 if equal, positive if a is after b.
 *
 * Prefer this over comparing raw UTC instants for server-side date-ordering guards:
 * both "received" and "consumed" dates are stored as org-midnight UTC, so their
 * instants are equal when the calendar days are equal — but that assumption would
 * break if a future writer stores a non-midnight instant. Calendar-day comparison
 * is resilient to that.
 *
 * @see docs/TIMEZONE_STANDARDS.md — "Comparing Calendar Days"
 */
export const compareCalendarDays = (a: CalendarDay, b: CalendarDay): number => {
  const isoA = calendarDayToISO(a);
  const isoB = calendarDayToISO(b);
  if (isoA < isoB) {
    return -1;
  }
  if (isoA > isoB) {
    return 1;
  }
  return 0;
};

/**
 * Format a DatePicker value for display as the calendar day the user selected.
 * Use for labels like "Balance as of 01/31/2026" — NOT for formatting instants/timestamps
 * (use formatDateAsDate with timezone for those).
 *
 * Per TIMEZONE_STANDARDS: the picker shows the day in browser local time. If we format
 * the raw Date instant in org timezone, users ahead of org see the previous day.
 */
export const formatPickerDateForDisplay = (pickerDate: Date): string => {
  const day = extractCalendarDayLocal(pickerDate);
  return DateTime.fromObject({ year: day.year, month: day.month, day: day.day }).toFormat("MM/dd/yyyy");
};

// ============================================================================
// API-Ready Date Boundary Utilities
// ============================================================================
//
// ShelfCycle Standard: Org timezone should ALWAYS drive date calculations, NEVER the
// local browser timezone nor raw UTC. These utilities create timestamps suitable for
// API calls (stored as UTC but calculated from org timezone boundaries).
//
// Use these for:
// - Date picker onChange handlers that send dates to APIs
// - Calculating min/max date constraints
// - Any date range filters passed to backend queries
//
// The pattern: Calculate boundary in org timezone → Convert to UTC → Return as JS Date
// This ensures the UTC timestamp represents the correct moment (e.g., end of Dec 31
// in Central Time is Jan 1 06:00 UTC, not Dec 31 midnight UTC).

/**
 * Get end-of-day for a calendar day in the given timezone, returned as a UTC Date.
 * Use this for "balance as of" or "transactions up to" date queries.
 *
 * This is the type-safe version that accepts an explicit calendar day.
 *
 * @param day - The calendar day (year, month, day)
 * @param timezone - IANA timezone string (e.g., "America/Chicago", "US/Central")
 * @returns UTC Date representing end of that day in the specified timezone
 *
 * @example
 * // User selects Dec 31 in date picker, org is in US/Central
 * const day = extractCalendarDayLocal(selectedDate); // Get day user SAW in picker
 * const balanceAt = calendarDayEndOfDayUTC(day, timezone);
 * // balanceAt is Jan 1 05:59:59.999 UTC (Dec 31 23:59:59.999 CST)
 */
export const calendarDayEndOfDayUTC = (day: CalendarDay, timezone: string): Date =>
  DateTime.fromObject({ year: day.year, month: day.month, day: day.day }, { zone: timezone }).endOf("day").toUTC().toJSDate();

/**
 * Get start-of-day for a calendar day in the given timezone, returned as a UTC Date.
 * Use this for "transactions from" or period start date queries.
 *
 * @param day - The calendar day (year, month, day)
 * @param timezone - IANA timezone string
 * @returns UTC Date representing start of that day in the specified timezone
 */
export const calendarDayStartOfDayUTC = (day: CalendarDay, timezone: string): Date =>
  DateTime.fromObject({ year: day.year, month: day.month, day: day.day }, { zone: timezone }).startOf("day").toUTC().toJSDate();

/**
 * Get a Luxon DateTime for end-of-day of a calendar day in the given timezone, in UTC.
 * Use when you need the DateTime object for comparisons (e.g., minDate > maxDate checks).
 */
export const calendarDayEndOfDayDateTime = (day: CalendarDay, timezone: string): DateTime =>
  DateTime.fromObject({ year: day.year, month: day.month, day: day.day }, { zone: timezone }).endOf("day").toUTC();

/**
 * Get a Luxon DateTime for start-of-day of a calendar day in the given timezone, in UTC.
 */
export const calendarDayStartOfDayDateTime = (day: CalendarDay, timezone: string): DateTime =>
  DateTime.fromObject({ year: day.year, month: day.month, day: day.day }, { zone: timezone }).startOf("day").toUTC();

// ============================================================================
// Convenience Functions for DatePicker Integration
// ============================================================================
//
// These combine extractCalendarDayLocal + calendarDay* for common DatePicker patterns.
// They extract the calendar day the user SAW (in browser timezone) and convert to
// the org timezone boundary.
//
// IMPORTANT: These assume the DatePicker shows dates in browser local time (standard behavior).

/**
 * Convert a DatePicker value to end-of-day in org timezone, returned as UTC.
 *
 * Extracts the calendar day the user SAW in the picker (browser local time),
 * then calculates end-of-day for that date in the org timezone.
 *
 * @param pickerDate - Date from DatePicker onChange
 * @param timezone - Org timezone
 * @returns UTC Date representing end of the selected day in org timezone
 *
 * @example
 * <DateInput
 *   onChange={(value) => {
 *     if (value) setBalanceAt(pickerDateToEndOfDayUTC(value, timezone));
 *   }}
 * />
 */
export const pickerDateToEndOfDayUTC = (pickerDate: Date, timezone: string): Date => {
  const day = extractCalendarDayLocal(pickerDate);
  return calendarDayEndOfDayUTC(day, timezone);
};

/**
 * Convert a DatePicker value to start-of-day in org timezone, returned as UTC.
 */
export const pickerDateToStartOfDayUTC = (pickerDate: Date, timezone: string): Date => {
  const day = extractCalendarDayLocal(pickerDate);
  return calendarDayStartOfDayUTC(day, timezone);
};

/**
 * Convert a DatePicker value to end-of-day DateTime in org timezone.
 * Use when you need the DateTime object for comparisons.
 */
export const pickerDateToEndOfDayDateTime = (pickerDate: Date, timezone: string): DateTime => {
  const day = extractCalendarDayLocal(pickerDate);
  return calendarDayEndOfDayDateTime(day, timezone);
};

/**
 * Convert a DatePicker value to start-of-day DateTime in org timezone.
 */
export const pickerDateToStartOfDayDateTime = (pickerDate: Date, timezone: string): DateTime => {
  const day = extractCalendarDayLocal(pickerDate);
  return calendarDayStartOfDayDateTime(day, timezone);
};

// ============================================================================
// "Now" Utilities
// ============================================================================

/**
 * Get the current moment as end-of-day in the given timezone, returned as a UTC Date.
 * Useful for "as of now" or max date constraints.
 */
export const nowEndOfDayUTC = (timezone: string): Date => DateTime.now().setZone(timezone).endOf("day").toUTC().toJSDate();

/**
 * Get the current moment as start-of-day in the given timezone, returned as a UTC Date.
 * Useful for "today" or min date constraints.
 */
export const nowStartOfDayUTC = (timezone: string): Date => DateTime.now().setZone(timezone).startOf("day").toUTC().toJSDate();

/**
 * Get current DateTime as end-of-day in timezone, already in UTC.
 */
export const nowEndOfDayDateTime = (timezone: string): DateTime => DateTime.now().setZone(timezone).endOf("day").toUTC();

/**
 * Get current DateTime as start-of-day in timezone, already in UTC.
 */
export const nowStartOfDayDateTime = (timezone: string): DateTime => DateTime.now().setZone(timezone).startOf("day").toUTC();

// ============================================================================
// DEPRECATED - Legacy Functions (use picker* or calendarDay* versions instead)
// ============================================================================
//
// These accept a Date and interpret it in a timezone, which has the semantic
// ambiguity problem described above. Kept for backwards compatibility.

/**
 * @deprecated Use pickerDateToEndOfDayUTC() for DatePicker values or
 * calendarDayEndOfDayUTC() for explicit calendar days.
 *
 * This function has ambiguous semantics - it interprets the Date's instant
 * in the given timezone, which may give unexpected results if the Date
 * was created in a different timezone.
 */
export const toEndOfDayUTC = (date: Date, timezone: string): Date => pickerDateToEndOfDayUTC(date, timezone);

/**
 * @deprecated Use pickerDateToStartOfDayUTC() for DatePicker values or
 * calendarDayStartOfDayUTC() for explicit calendar days.
 */
export const toStartOfDayUTC = (date: Date, timezone: string): Date => pickerDateToStartOfDayUTC(date, timezone);

/**
 * @deprecated Use pickerDateToEndOfDayDateTime() for DatePicker values or
 * calendarDayEndOfDayDateTime() for explicit calendar days.
 */
export const toEndOfDayDateTime = (date: Date, timezone: string): DateTime => pickerDateToEndOfDayDateTime(date, timezone);

/**
 * @deprecated Use pickerDateToStartOfDayDateTime() for DatePicker values or
 * calendarDayStartOfDayDateTime() for explicit calendar days.
 */
export const toStartOfDayDateTime = (date: Date, timezone: string): DateTime => pickerDateToStartOfDayDateTime(date, timezone);
